import { useEffect, useState } from 'react';
import { Button, Form, Col, Row, Divider, Drawer, Checkbox, Input, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { drawerWidth, multiOrderUpdateFields, selectOptionsTypes } from '../../../utils/constants';
import DynamicSelect from '../../../components/common/dynamicSelect';
import CustomerOrdersServices from '../../../services/customerOrdersServices';

const MultiUpdateOrders = ({ visibility, onClose, selectedOrderIds, selectedItemIds, afterOrderListUpdate }: { visibility: boolean, onClose: () => void, selectedOrderIds: string[], selectedItemIds: string[], afterOrderListUpdate: () => void }) => {

    const [form] = Form.useForm();
    const { t } = useTranslation();
    const [loadingUpdateOrders, setLoadingUpdateOrders] = useState(false);
    const [ordersFieldsToUpdate, setOrdersFieldsToUpdate] = useState<{ label: string, value: string }[]>([]);
    const [selectedFieldsToUpdate, setSelectedFieldsToUpdate] = useState<string[]>([]);
    const [viewUpdateFields, setViewUpdateFields] = useState(false);

    useEffect(() => {
        setOrdersFieldsToUpdate([
            { label: t('order.material'), value: multiOrderUpdateFields.material },
            { label: t('order.address'), value: multiOrderUpdateFields.address },
            { label: t('order.city'), value: multiOrderUpdateFields.city },
            { label: t('order.region'), value: multiOrderUpdateFields.region },
            { label: t('order.postalCode'), value: multiOrderUpdateFields.postalCode }
        ]);
    }, []);

    const onDrawerClose = () => {
        onClose();
        setViewUpdateFields(false);
        setSelectedFieldsToUpdate([]);
        form.resetFields();
    }


    const onSubmit = async () => {
        let payload: { fieldsToUpdate: { [key: string]: string }, orderHeaderIds: string, orderItems: string } = { fieldsToUpdate: {}, orderHeaderIds: "", orderItems: "" };
        const values = form.getFieldsValue();
        payload["fieldsToUpdate"] = values;
        payload["orderHeaderIds"] = selectedOrderIds.join(",");
        payload["orderItems"] = selectedItemIds.join(",");

        try {
            setLoadingUpdateOrders(true);
            const respData = await CustomerOrdersServices.multiUpdateOrders(payload);

            if (respData.success) {
                message.success(t('messages.success'));
                onDrawerClose();
                afterOrderListUpdate();
            } else {
                message.error(t('messages.errorAccured'));
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        } finally {
            setLoadingUpdateOrders(false);
        }

    };


    const FieldsToUpdate = ({ fieldsToUpdate }: { fieldsToUpdate: string[] }) => {
        return (
            <Form
                form={form}
                layout='vertical'
                name="uploadOrder"
                autoComplete="off"
                onFinish={onSubmit}
            >
                <Row gutter={[16, 8]} className='mb10'>
                    {fieldsToUpdate.includes(multiOrderUpdateFields.material) && (
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('order.material')}
                                name={multiOrderUpdateFields.material}
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <DynamicSelect type={selectOptionsTypes.material} value={undefined} onChange={undefined} onSelectChange={undefined} location="" />
                            </Form.Item>
                        </Col>
                    )}

                    {fieldsToUpdate.includes(multiOrderUpdateFields.address) && (
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('order.address')}
                                name={multiOrderUpdateFields.address}
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    )}

                    {fieldsToUpdate.includes(multiOrderUpdateFields.city) && (
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('order.city')}
                                name={multiOrderUpdateFields.city}
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    )}

                    {fieldsToUpdate.includes(multiOrderUpdateFields.region) && (
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('order.region')}
                                name={multiOrderUpdateFields.region}
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    )}

                    {fieldsToUpdate.includes(multiOrderUpdateFields.postalCode) && (
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('order.postalCode')}
                                name={multiOrderUpdateFields.postalCode}
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
                <Divider />
                <Form.Item>
                    <Button htmlType='submit' loading={loadingUpdateOrders} block icon={<SaveOutlined />}>{t('general.save')}</Button>
                </Form.Item>
            </Form>
        )
    }

    const handleFieldsToUpdateChange = (fields: string[]) => {
        setSelectedFieldsToUpdate(fields);
    }

    return (
        <Drawer
            title={t('order.updateOrders')}
            width={drawerWidth}
            onClose={onDrawerClose}
            destroyOnClose={true}
            open={visibility}
        >
            {!viewUpdateFields && (
                <Row gutter={[16, 16]}>
                    <Divider orientation='left' >{t('order.fieldsToUpdate')}</Divider>
                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                        <Checkbox.Group onChange={(value) => handleFieldsToUpdateChange(value)} options={ordersFieldsToUpdate.map((item) => ({ label: item.label, value: item.value }))} />
                    </Col>
                    <Divider />
                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                        <Button type='default' block onClick={() => setViewUpdateFields(true)}>{t('general.next')}</Button>
                    </Col>
                </Row>
            )}

            {viewUpdateFields && (
                <FieldsToUpdate fieldsToUpdate={selectedFieldsToUpdate} />
            )}

        </Drawer>
    );
}
export default MultiUpdateOrders;